import { useEffect, useMemo, useState } from "react";

import { ensureCsrfToken } from "../../api";

import Swal from "sweetalert2";




const BLOCKING_ROWS = [
  { key: "pan_inactive", label: "Vendor PAN Inactive" },
  { key: "aadhar_pan", label: "Vendor Aadhar & PAN linked (Individual & Proprietor)" },
  { key: "gst_inactive", label: "GST Inactive" },
  { key: "gstr1", label: "GSTR1 not Filed", hasMonths: true },
  { key: "gstr3b", label: "GSTR3B Filed (not filed flag)", hasMonths: true },
  { key: "gst_2b", label: "GST not appearing in 2B (13 days rule)", note: "Under Development" },
  { key: "new_vendor", label: "New Vendor / Irregular Transactions" },
  { key: "regular_vendor", label: "Regular Vendor" },
  { key: "prev_gst", label: "Previous invoice GST not appearing" },
  
];


const DEFAULT_OPEN_SECTIONS = {
  baseline: true,
  blocking: false,
  threshold: false,
};



function createDefaultConfig() {

  
  const blocking = BLOCKING_ROWS.reduce((accumulator, row) => {
    accumulator[row.key] = {
      acc: row.key === "gst_inactive" ? "Y" : "N",
      pay: row.key === "regular_vendor" ? "N" : "Y",
      months: "",
      code: "",
    };
    return accumulator;
  }, {});

  return {
    
    baseline: {
      baseline_active: "N",
      baseline_choice: "posting",
    },
    blocking,
    blocking_activate: {
      account_blocking_activate: "Y",
      payment_blocking_activate: "Y",
      blocked_vendor_code: "X,Yes,Inactive,Block,Blocked",
      payment_block_codes: "X,Yes,Inactive,Block,Blocked",
      statutory_block_activate: "Y",
    },
    
    
    threshold: {
      turnover_above_threshold: "Y",
      threshold_amount: "5000000",
      wt_rate: "0.1",
    },
    
  };
}

function mergeLoadedConfig(payload) {
  const next = createDefaultConfig();
  if (!payload) {
    return next;
  }

  

  if (payload.baseline) {
    next.baseline = { ...next.baseline, ...payload.baseline };
  }

  if (payload.blocking) {
    BLOCKING_ROWS.forEach((row) => {
      next.blocking[row.key] = {
        ...next.blocking[row.key],
        ...(payload.blocking[row.key] || {}),
      };
    });
  }

  if (payload.blocking_activate) {
    next.blocking_activate = {
      ...next.blocking_activate,
      ...payload.blocking_activate,
    };
  }
  
  

  if (payload.threshold) {
    next.threshold = { ...next.threshold, ...payload.threshold };
  }

  
  return next;
}

async function parseJsonResponse(response) {
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) {
    return {
      isJson: true,
      payload: await response.json(),
      text: "",
    };
  }
  return {
    isJson: false,
    payload: null,
    text: await response.text(),
  };
}

function normalizeNonJsonError(response, text, fallback) {
  const body = String(text || "");
  const lower = body.toLowerCase();
  const looksLikeHtml = lower.includes("<html") || lower.includes("<!doctype html");
  const looksLikeLogin = lower.includes("miro app login") || lower.includes("name=\"password\"");

  if (response.redirected || response.url.includes("next=") || looksLikeLogin) {
    return "Session is not authenticated. Please login again.";
  }
  if (looksLikeHtml) {
    return fallback;
  }
  return body || fallback;
}

function Section({ title, subtitle, open, onToggle, children }) {
  return (
    <article className="config-card">
      <button className="config-card__head" type="button" onClick={onToggle}>
        <div>
          <div className="config-card__title">{title}</div>
          <div className="config-card__subtitle">{subtitle}</div>
        </div>
        <span className="config-card__toggle">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? <div className="config-card__body">{children}</div> : null}
    </article>
  );
}

function RadioGroup({ name, value, options, onChange }) {
  return (
    <div className="config-inline">
      {options.map((option) => (
        <label key={option.value} className="config-choice">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function ApConfigurationView() {
  const [config, setConfig] = useState(createDefaultConfig);
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");


  useEffect(() => {
    let cancelled = false;

    async function loadConfiguration() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch("/api/configuration/", {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });
        const { isJson, payload, text } = await parseJsonResponse(response);
        if (!isJson) {
          throw new Error(
            normalizeNonJsonError(
              response,
              text,
              "Unable to load saved configuration.",
            ),
          );
        }

        if (!cancelled) {
          if (payload.status === "success") {            
            setConfig(mergeLoadedConfig(payload.data));
          }
          setStatus("");
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load saved configuration.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadConfiguration();

    return () => {
      cancelled = true;
    };
  }, []);

  function toggleSection(sectionKey) {
    setOpenSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  }

  

  function updateSection(sectionKey, field, value) {
    setConfig((current) => ({
      ...current,
      [sectionKey]: {
        ...current[sectionKey],
        [field]: value,
      },
    }));
  }


  function updateBlocking(rowKey, field, value) {
    setConfig((current) => ({
      ...current,
      blocking: {
        ...current.blocking,
        [rowKey]: {
          ...current.blocking[rowKey],
          [field]: value,
        },
      },
    }));
  }

  

  function buildPayload() {
    return {
      
      baseline: config.baseline,
      blocking: config.blocking,
      threshold: config.threshold,
      
    };
  }

  async function handleSave() {
    setSaving(true);
    
    try {
      
      
      const csrfToken = await ensureCsrfToken();
      const formData = new FormData();
      formData.append("payload", JSON.stringify(buildPayload()));

      const response = await fetch("/api/configuration/save/", {
        method: "POST",
        headers: {
          "X-CSRFToken": csrfToken,
          Accept: "application/json",
        },
        credentials: "include",
        body: formData,
      });

      const { isJson, payload, text } = await parseJsonResponse(response);
      if (!isJson) {
        throw new Error(
          normalizeNonJsonError(
            response,
            text,
            "Unable to save configuration.",
          ),
        );
      }

      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || "Unable to save configuration.");
      }
      Swal.fire({
        icon: "success",
        title: "Success",
        text: payload.message || "Configuration saved successfully.",
        confirmButtonText: "OK",
      });
      
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: requestError.message || "Unable to save configuration.",
        confirmButtonText: "OK",
      });
      
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="workspace-panel">Loading configuration…</div>;
  }

  return (
    <section className="config-view">
      <div className="config-view__header workspace-panel">
        <div>
          <p className="eyebrow">Basic Configurations</p>
          <h2>Accounting Configuration</h2>
          <p className="config-view__intro">
            This is a common configuration for both PO & Non PO AP.
          </p>
        </div>
        <button className="primary-button" type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Submit Configuration"}
        </button>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      

      <Section
        title="1. Baseline Date"
        subtitle="Activate baseline date"
        open={openSections.baseline}
        onToggle={() => toggleSection("baseline")}
      >
        <div className="config-grid">
          <div className="config-field">
            <label>Activate Baseline date?</label>
            <RadioGroup
              name="baseline_active"
              value={config.baseline.baseline_active}
              options={[
                { value: "N", label: "No" },
                { value: "Y", label: "Yes" },
              ]}
              onChange={(value) => updateSection("baseline", "baseline_active", value)}
            />
          </div>

          {config.baseline.baseline_active === "Y" ? (
            <div className="config-field">
              <label>If Yes, baseline date should be</label>
              <select
                value={config.baseline.baseline_choice}
                onChange={(event) => updateSection("baseline", "baseline_choice", event.target.value)}
              >
                <option value="posting">Posting Date</option>
                <option value="invoice">Invoice Date</option>
                <option value="migo">GRN Date</option>
              </select>
            </div>
          ) : null}
        </div>
      </Section>

      <Section
        title="2. Flags for Blocking"
        subtitle="Block accounting or payment from vendor PAN and GST signals"
        open={openSections.blocking}
        onToggle={() => toggleSection("blocking")}
      >
        <div className="config-field">
          <label>A. Activate Block from Vendor Master</label>
          <RadioGroup
            name="vendor_master_block"
            value={config.blocking_activate.account_blocking_activate}
            options={[
              { value: "Y", label: "Y" },
              { value: "N", label: "N" },
            ]}
            onChange={(value) =>
              updateSection("blocking_activate", "account_blocking_activate", value)
            }
          />

          {config.blocking_activate.account_blocking_activate === "Y" && (
            <input
              type="text"
              className="config-input"
              placeholder="Enter codes separated by commas"
              value={config.blocking_activate.blocked_vendor_code ?? ""}
              onChange={(e) =>
                updateSection(
                  "blocking_activate",
                  "blocked_vendor_code",
                  e.target.value
                )
              }
            />
          )}
        </div>

        <div className="config-field">
          <label>B. Activate Block from GRN Report</label>
          <RadioGroup
            name="grn_block"
            value={config.blocking_activate.payment_blocking_activate}
            options={[
              { value: "Y", label: "Y" },
              { value: "N", label: "N" },
            ]}
            onChange={(value) =>
              updateSection("blocking_activate", "payment_blocking_activate", value)
            }
          />

          {config.blocking_activate.payment_blocking_activate === "Y" && (
            <input
              type="text"
              className="config-input"
              placeholder="Enter codes separated by commas"
              value={config.blocking_activate.payment_block_codes ?? ""}
              onChange={(e) =>
                updateSection(
                  "blocking_activate",
                  "payment_block_codes",
                  e.target.value
                )
              }
            />
          )}
        </div>

        <div className="config-table">
          <label>C. Activate Statutory Block</label>

          <div className="config-field">
            <RadioGroup
              name="statutory_block_activate"
              value={config.blocking_activate.statutory_block_activate ?? "N"}
              options={[
                { value: "Y", label: "Y" },
                { value: "N", label: "N" },
              ]}
              onChange={(value) => {
                updateSection(
                  "blocking_activate",
                  "statutory_block_activate",
                  value
                );

                if (value === "N") {
                  BLOCKING_ROWS.forEach((row) => {
                    updateBlocking(row.key, "acc", "N");
                    updateBlocking(row.key, "pay", "N");
                  });
                }
              }}
            />
          </div>

          {config.blocking_activate.statutory_block_activate === "Y" && (
            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Accounting</th>
                  <th>Payment</th>
                  <th>Code</th>
                  <th>Months</th>
                </tr>
              </thead>
              <tbody>
                {BLOCKING_ROWS.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>
                      <select
                        value={config.blocking[row.key].acc}
                        onChange={(event) => updateBlocking(row.key, "acc", event.target.value)}
                      >
                        <option value="N">N</option>
                        <option value="Y">Y</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={config.blocking[row.key].pay}
                        onChange={(event) => updateBlocking(row.key, "pay", event.target.value)}
                      >
                        <option value="N">N</option>
                        <option value="Y">Y</option>
                      </select>
                    </td>
                    <td>
                      <input
                        value={config.blocking[row.key].code}
                        maxLength={3}
                        onChange={(event) => updateBlocking(row.key, "code", event.target.value.toUpperCase())}
                      />
                    </td>
                    <td>
                      {row.hasMonths ? (
                        <select
                          value={config.blocking[row.key].months}
                          onChange={(event) => updateBlocking(row.key, "months", event.target.value)}
                        >
                          <option value="">-</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                        </select>
                      ) : (
                        <span className="config-note">{row.note || "-"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
          
        <div className="config-field">
          <label>D. GST Input Credit Expiry Date</label>

          <input
            type="text"
            className="config-input"
            placeholder="DD/MM"
            maxLength={5}
            value={config.gst_input_expired ?? ""}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, "");

              if (value.length > 2) {
                value = value.slice(0, 2) + "/" + value.slice(2, 4);
              }

              setConfig((prev) => ({
                ...prev,
                gst_input_expired: value,
              }));
            }}
          />
        </div>

        <div className="config-field">
          <label>E. Tax Prior Period Cut-off Date</label>

          <input
            type="text"
            className="config-input"
            placeholder="DD/MM"
            maxLength={5}
            value={config.tax_period_expired ?? ""}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, "");

              if (value.length > 2) {
                value = value.slice(0, 2) + "/" + value.slice(2, 4);
              }

              setConfig((prev) => ({
                ...prev,
                tax_period_expired: value,
              }));
            }}
          />
        </div>

        
      </Section>

      

      <Section
        title="3. Threshold Check (u/s 194Q)"
        subtitle="Check if turnover exceeds the threshold"
        open={openSections.threshold}
        onToggle={() => toggleSection("threshold")}
      >
        <div className="config-grid">
          <div className="config-field">
            <label>Is company turnover above threshold limit?</label>
            <RadioGroup
              name="turnover_above_threshold"
              value={config.threshold.turnover_above_threshold}
              options={[
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ]}
              onChange={(value) => updateSection("threshold", "turnover_above_threshold", value)}
            />
          </div>

          {config.threshold.turnover_above_threshold === "Y" ? (
            <>
              <div className="config-field">
                <label>Threshold Amount</label>
                <input
                  type="number"
                  value={config.threshold.threshold_amount}
                  onChange={(event) => updateSection("threshold", "threshold_amount", event.target.value)}
                />
              </div>
              <div className="config-field">
                <label>WT Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={config.threshold.wt_rate}
                  onChange={(event) => updateSection("threshold", "wt_rate", event.target.value)}
                />
              </div>
            </>
          ) : null}
        </div>
      </Section>

  
    </section>
  );
}
